/**
 * AtlasVenueModule — ONE WORKING VENUE: the town's tavern is a building, and its counter is the publican's post
 * (the interiors programme, slice E, 2026-10-01).
 *
 * A town's venues were POINTS (the tavern slot of the town plan): the publican's post, its duty radius and its
 * customers all measured from the slot, and the tavern BUILDING hosted nothing. `lib/venue_binding.ts` (mirrored on
 * the server) names the ONE enterable building of a site that is its tavern, and its clinic; the server works the
 * post inside it (`reducers/player_posts.ts`). This module is the client's half:
 *
 *   THE LEVER      reads `platform_flag interior_venues_v1` (ABSENT = ON) → `venue_client.setVenuesEnabled`. Off, this
 *                  module does nothing and undoes what it did: every building keeps its civ name and purpose, every
 *                  post is at its slot, the maps read as before.
 *   THE BUILDING   a bound building in the enterable registry wears the venue's name at its door (`b.name`, the
 *                  original kept and restored), resolves to the venue's purpose (`setPurposeOverride`: the visit then
 *                  plans a cantina with a counter, a clinic with cots) and its sign reads the canonical name
 *                  (`setInteriorVenueResolver`). The maps name it the same (`setMapVenueNamer`).
 *   THE COUNTER    inside the bound room it resolves the staff / customer / queue anchors from the visit's active
 *                  descriptor (`venue_anchor.venueAnchors`, with the legacy and shell fallbacks) and publishes them
 *                  (`venue_client.venueCounter()`): the Town module's serve prompt reaches from the staff side, and
 *                  the life layer presents the staff and the arrived customer there.
 *   E              ONE KEY, ONE FOCUS: behind a free counter "take the post"; before it, who keeps it (E opens the
 *                  Service posts tab). Serving stays the Town module's `post:serve` claim.
 * No coin, credit or rule lives here: every act is an existing reducer. Lever: `__venue()`.
 */
import type { App, IModule } from 'enginex';
import type { Identity } from 'spacetimedb';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { interactFocus } from '../lib/interact_focus.js';
import { onInteract, interactKey } from '../lib/interact_input.js';
import { enterables, interiorSpec, type EnterableBuilding } from '../lib/building_interiors.js';
import { setPurposeOverride } from '../lib/interior_purpose.js';
import { setMapVenueNamer } from '../lib/map_structures.js';
import { activeInterior, activeDescriptorFor } from '../lib/interior_wiring.js';
import { setInteriorVenueResolver } from './interior/interior_look.js';
import { servicePost } from '../lib/service_posts.js';
import { settlementSites } from '../../../shared/settlements.js';
import { nameFromTables } from '../lib/versona_names.js';
import { VENUE_FLAG, VENUE_ARRIVE_M, venueDef, venueAtSlot, insideVenue, type VenueBinding } from '../lib/venue_binding.js';
import { venueAnchors, counterOffer, type VenueAnchors, type WorldAnchor } from '../lib/venue_anchor.js';
import {
  venueBindings, venueFlagFrom, venuesEnabled, setVenuesEnabled, venueOfEnterable, venueOfPostRow, venueTitle, venueDoorName,
  venuePurpose, mapVenueLabel, setVenueCounter, venueCounter, type VenueSpot,
} from '../lib/venue_client.js';

interface PostRow { identity: Identity; settlementId: bigint; postKey: string; venueX: number; venueZ: number }
interface RequestRow { requestId: bigint; holder: Identity; versonaId: bigint; need: string; expiresAt: { microsSinceUnixEpoch: bigint } }
interface SlotRow { settlementId: bigint; recipeKey: string; holderId: bigint }
interface StateRow { versonaId: bigint; curX: number; curZ: number; state: string }
interface Db {
  platformFlag?: { key: { find(k: string): { enabled: boolean } | undefined } };
  playerPost?: { identity: { find(i: Identity): PostRow | undefined }; iter(): Iterable<PostRow> };
  postRequest?: { iter(): Iterable<RequestRow> };
  jobSlot?: { iter(): Iterable<SlotRow> };
  versonaState?: { versonaId: { find(i: bigint): StateRow | undefined } };
  account?: { identity: { find(i: Identity): { displayName: string; username: string } | undefined } };
  world?: { iter(): Iterable<{ kind: string; terrainSeed: string }> };
}

/** One bound building in the registry: what this module changed on it, so it can be put back. */
interface Applied { ent: EnterableBuilding; binding: VenueBinding; orig: string; set: string }

const SCAN_MS = 600;
const postLabel = (key: string): string => (key.startsWith('svc:') ? key.slice(4) : key).replace(/_/g, ' ');

/** venue_anchor's labels lead with a literal "E — "; show the BOUND key (F2/G6, 2026-10-05). */
const boundLabel = (l: string): string => l.replace(/^E — /, `${interactKey()} — `);

export class AtlasVenueModule implements IModule {
  readonly name = 'AtlasVenue';
  private applied = new Map<string, Applied>();
  private seed = '';
  private scanAt = 0;
  private subbed = false;
  private slotSubFor = -1;
  private prompt: HTMLDivElement | null = null;
  private offer: ReturnType<typeof counterOffer> = null;
  private offerPost = '';
  /** The anchors of the room the player stands in, cached per (interior, descriptor version). */
  private cache: { id: string; ver: number; anchors: VenueAnchors } | null = null;
  /** F2: the bound Interact key AND the pad's (`atlas:place-requested`), one press — lib/interact_input.ts. */
  private offInteract: (() => void) | null = null;

  init(app: App): void {
    // The sign inside the door: a bound tavern reads its canonical name (a clinic has none — its purpose is its sign).
    setInteriorVenueResolver((look) => (venueOfEnterable(this.seed, look)?.kind === 'tavern' ? 'tavern' : null));
    setMapVenueNamer((site, index) => mapVenueLabel(this.seed, site, index));
    bus.on('atlas:account-ready', () => this.subscribe());
    if (atlas.conn) this.subscribe();
    bus.on('atlas:world-cleared', () => { this.seed = ''; this.scanAt = 0; });
    const pr = document.createElement('div');
    pr.id = 'venue-prompt';
    pr.style.cssText = 'position:fixed;left:50%;bottom:262px;transform:translateX(-50%);z-index:41;display:none;'
      + 'padding:6px 14px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg) 86%, transparent);border:1px solid color-mix(in srgb, var(--ax-accent) 55%, transparent);'
      + 'font:600 13px var(--ax-font);color:var(--ax-text);pointer-events:none';
    document.body.appendChild(pr);
    this.prompt = pr;
    this.offInteract = onInteract(() => this.key());
    // Before the Town module's post tick (UI/39): it reads the counter this publishes.
    app.world.addSystem(makeSystem('VenueCounter', Phase.UI, 38, () => this.tick()));
    (window as unknown as Record<string, unknown>).__venue = () => this.state();
  }

  dispose(): void {
    this.offInteract?.(); this.offInteract = null;
    this.restoreAll();
    setInteriorVenueResolver(null);
    setMapVenueNamer(null);
    setVenueCounter(null);
    this.prompt?.remove();
    const w = window as unknown as Record<string, unknown>;
    delete w.__venue;
  }

  private db(): Db | null { const c = atlas.conn; return c ? (c.db as unknown as Db) : null; }

  private subscribe(): void {
    const conn = atlas.conn;
    if (!conn || this.subbed) return;
    this.subbed = true;
    try { conn.subscriptionBuilder().subscribe(['SELECT * FROM platform_flag']); } catch { /* reconnect */ }
  }

  // ── The scan: the lever, and the bound buildings in the registry ───────────────────────────────────────────────────
  private scan(): void {
    const db = this.db();
    let on = true;
    try { on = venueFlagFrom(db?.platformFlag?.key.find(VENUE_FLAG)); } catch { on = true; }
    if (on !== venuesEnabled()) { setVenuesEnabled(on); this.cache = null; }
    if (!this.seed && db?.world) { try { for (const w of db.world.iter()) if (w.kind === 'commons') { this.seed = w.terrainSeed; break; } } catch { /* rows mid-churn */ } }
    const inCommons = atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId;
    const wanted = new Map<string, VenueBinding>();
    if (on && inCommons && this.seed && venueBindings(this.seed).length > 0) {
      for (const e of enterables.values()) {
        if (!e.id.startsWith('settle:')) continue;
        const b = venueOfEnterable(this.seed, e);
        if (b) wanted.set(e.id, b);
      }
    }
    for (const [id, b] of wanted) {
      const e = enterables.get(id)!;
      const a = this.applied.get(id);
      if (a && a.ent === e && e.name === a.set) continue;
      // a rebuilt site registers a fresh entry under the same id: name it again (the original is the entry's own)
      const set = venueDoorName(b);
      const orig = a && a.ent === e ? a.orig : e.name;
      e.name = set;
      setPurposeOverride(id, venuePurpose(b));
      this.applied.set(id, { ent: e, binding: b, orig, set });
    }
    for (const [id, a] of [...this.applied]) {
      if (wanted.has(id)) continue;
      this.restore(id, a);
    }
  }

  private restore(id: string, a: Applied): void {
    if (enterables.get(id) === a.ent && a.ent.name === a.set) a.ent.name = a.orig;
    setPurposeOverride(id, null);
    this.applied.delete(id);
  }
  private restoreAll(): void { for (const [id, a] of [...this.applied]) this.restore(id, a); }

  // ── The frame: the counter of the bound room the player stands in ─────────────────────────────────────────────────
  private tick(): void {
    const now = performance.now();
    if (now - this.scanAt > SCAN_MS) { this.scanAt = now; this.scan(); }
    const id = atlas.interiorId;
    const a = id ? this.applied.get(id) : undefined;
    if (!id || !a) { if (venueCounter()) setVenueCounter(null); this.cache = null; this.show(null); this.offer = null; return; }
    const act = activeInterior();
    const ver = act && act.id === id ? act.version : -1;
    if (!this.cache || this.cache.id !== id || this.cache.ver !== ver) {
      const ent = a.ent;
      const spec = interiorSpec(ent.w, ent.h, ent.d, ent.key);
      const live = activeDescriptorFor(id);
      // the pieces the visit really seated on the ground floor (a hidden or unpacked piece is no counter)
      const shown = live?.view ? new Set(live.view.floors.filter((f) => f.floor === 0).flatMap((f) => f.pieces.filter((p) => p.shown).map((p) => p.slot))) : null;
      const anchors = venueAnchors(a.binding.kind, live?.descriptor ?? null, { x: ent.x, z: ent.z, yaw: ent.yaw, groundY: ent.groundY, hw: spec.hw, hd: spec.hd, stair: spec.stair }, shown);
      this.cache = { id, ver, anchors };
      this.ensureSlotSub(a.binding);
    }
    const anchors = this.cache.anchors;
    // The counter is on the ground floor (the server cannot tell floors apart yet): upstairs nothing is in reach.
    // …and nobody UNDER it either (a capsule that fell through the slab is 'inside' in x/z and nowhere near a counter).
    const feet = atlas.localY - a.ent.groundY;
    const onGround = feet < 2.4 && feet > -1.2;
    const near = (list: WorldAnchor[]): number => {
      let d = Infinity;
      if (onGround) for (const q of list) d = Math.min(d, Math.hypot(q.x - atlas.localX, q.z - atlas.localZ));
      return d;
    };
    const dStaff = near(anchors.staff), dCustomer = near(anchors.customer);
    const facts = this.facts(a.binding);
    this.lastFacts = facts;
    setVenueCounter({ id, binding: a.binding, anchors, dStaff, dCustomer, anywhere: anchors.source === 'shell', spots: this.spots(a.binding, anchors, facts.staffId) });
    const def = venueDef(a.binding.kind)!;
    const offer = counterOffer({
      venueName: venueTitle(a.binding), postLabel: postLabel(def.post), mine: facts.mine, holdOther: facts.holdOther, holder: facts.holder, staff: facts.staff,
      waiting: facts.waiting, dStaff, dCustomer, anywhere: anchors.source === 'shell',
    });
    // `post:serve` is the Town module's own claim (it reads the counter published above); this module claims the rest.
    this.offer = offer && offer.id !== 'post:serve' ? offer : null;
    this.offerPost = def.post;
    let label: string | null = null;
    if (this.offer) {
      interactFocus().claim({ id: this.offer.id, dist: this.offer.dist, priority: 3, label: boundLabel(this.offer.label) });
      if (interactFocus().owns(this.offer.id)) label = boundLabel(this.offer.label);
    }
    this.show(label);
  }

  /** Who holds this venue's post, who is waiting, and the town's own versona on it. */
  private lastFacts: ReturnType<AtlasVenueModule['facts']> | null = null;
  private facts(b: VenueBinding): { mine: boolean; holdOther: boolean; holder: string | null; staff: string | null; staffId: bigint | null; waiting: { need: string; arrived: boolean; versonaId: bigint } | null } {
    const out = { mine: false, holdOther: false, holder: null as string | null, staff: null as string | null, staffId: null as bigint | null, waiting: null as { need: string; arrived: boolean; versonaId: bigint } | null };
    const db = this.db();
    const me = atlas.conn?.identity;
    const def = venueDef(b.kind);
    if (!db || !def) return out;
    try {
      const list = venueBindings(this.seed);
      const mineRow = me ? db.playerPost?.identity.find(me) : undefined;
      if (mineRow) { if (venueOfPostRow(this.seed, mineRow) === b) out.mine = true; else out.holdOther = true; }
      for (const p of db.playerPost?.iter() ?? []) {
        if (p.postKey !== def.post || (me && p.identity.isEqual(me))) continue;
        if (venueAtSlot(list, b.kind, p.venueX, p.venueZ) !== b) continue;
        const acc = db.account?.identity.find(p.identity);
        out.holder = acc ? (acc.displayName || acc.username) : 'another traveller';
        break;
      }
      if (out.mine && me) {
        let best: RequestRow | null = null;
        for (const r of db.postRequest?.iter() ?? []) {
          if (!r.holder.isEqual(me) || Number(r.expiresAt.microsSinceUnixEpoch / 1000n) <= Date.now()) continue;
          if (!best || r.requestId < best.requestId) best = r;
        }
        if (best) {
          const s = db.versonaState?.versonaId.find(best.versonaId);
          out.waiting = { need: best.need, arrived: !!s && Math.hypot(s.curX - b.streetX, s.curZ - b.streetZ) <= VENUE_ARRIVE_M, versonaId: best.versonaId };
        }
      }
      // The town's own holder of the post, ON it now (the post's state), nearest this ward's slot (a capital's wards).
      const state = servicePost(def.post)?.state ?? '';
      let bd = 160;
      for (const j of db.jobSlot?.iter() ?? []) {
        if (j.recipeKey !== def.post || j.holderId === 0n) continue;
        const s = db.versonaState?.versonaId.find(j.holderId);
        if (!s || s.state !== state) continue;
        const d = Math.min(Math.hypot(s.curX - b.slotX, s.curZ - b.slotZ), Math.hypot(s.curX - b.streetX, s.curZ - b.streetZ));
        if (d < bd) { bd = d; out.staffId = j.holderId; }
      }
      if (out.staffId !== null) out.staff = nameFromTables(atlas.conn, out.staffId) || 'a resident';
    } catch { /* rows mid-churn: nobody is named this frame */ }
    return out;
  }

  /** PRESENTATION: the town's versona on the post stands at the staff anchor; my ARRIVED customer at the customer anchor. */
  private spots(b: VenueBinding, anchors: VenueAnchors, staffId: bigint | null): Map<string, VenueSpot> {
    const m = new Map<string, VenueSpot>();
    // THE NEVER-MIME LAW: a presented body STANDS at the anchor's standing point (the cot's foot, the desk's side) —
    // the post states (`serving`, `tending`, `seeking_service`) carry no sit or lie clip, so nobody is laid on a cot
    // or sat in a chair by this module (slice L, residents moving indoors, owns seated service).
    const put = (id: bigint, a: WorldAnchor | undefined, role: VenueSpot['role']): void => {
      if (a) m.set(id.toString(), { x: a.x, y: a.y, z: a.z, yaw: a.yaw, lying: false, role });
    };
    const db = this.db();
    try {
      // every arrived customer of THIS venue's holder (any player's), oldest at the counter, the rest in the queue
      const list = venueBindings(this.seed);
      const holders = new Set<string>();
      for (const p of db?.playerPost?.iter() ?? []) if (venueAtSlot(list, b.kind, p.venueX, p.venueZ) === b && venueDef(b.kind)?.post === p.postKey) holders.add(p.identity.toHexString());
      const arrived: RequestRow[] = [];
      for (const r of db?.postRequest?.iter() ?? []) {
        if (!holders.has(r.holder.toHexString())) continue;
        const s = db?.versonaState?.versonaId.find(r.versonaId);
        if (s && Math.hypot(s.curX - b.streetX, s.curZ - b.streetZ) <= VENUE_ARRIVE_M) arrived.push(r);
      }
      arrived.sort((x, y) => (x.requestId < y.requestId ? -1 : x.requestId > y.requestId ? 1 : 0));
      const line = [...anchors.customer.slice(0, 1), ...anchors.queue];
      arrived.forEach((r, i) => put(r.versonaId, line[i], i === 0 ? 'customer' : 'queue'));
      // the versona holder — unless a player keeps the counter (one body behind it)
      if (staffId !== null && holders.size === 0) put(staffId, anchors.staff[0], 'staff');
    } catch { /* rows mid-churn */ }
    return m;
  }

  /** The town's `job_slot` rows (who the versona publican is), subscribed once per settlement the player works in. */
  private ensureSlotSub(b: VenueBinding): void {
    const conn = atlas.conn;
    if (!conn || !this.seed) return;
    let sid = b.site + 1;
    try { const s = settlementSites(this.seed)[b.site]; if (s && s.tier === 'district' && s.capitalId !== undefined) sid = s.capitalId + 1; } catch { /* the site's own id */ }
    if (this.slotSubFor === sid) return;
    this.slotSubFor = sid;
    try { conn.subscriptionBuilder().subscribe([`SELECT * FROM job_slot WHERE settlement_id = ${sid}`]); } catch { /* the label then names no resident */ }
  }

  private show(text: string | null): void {
    const p = this.prompt;
    if (!p) return;
    const on = text !== null;
    if ((p.style.display !== 'none') !== on) p.style.display = on ? 'block' : 'none';
    if (on && p.textContent !== text) p.textContent = text;
  }

  private key(): void {
    const o = this.offer;
    if (!o || !interactFocus().owns(o.id)) return;
    if (o.action === 'take') {
      const r = atlas.conn?.reducers as unknown as { takeServicePost?(a: { postKey: string }): Promise<void> } | undefined;
      const label = postLabel(this.offerPost);
      r?.takeServicePost?.({ postKey: this.offerPost })
        .then(() => bus.emit('atlas:toast', { text: `You hold the ${label}'s post — work it from here; customers will come to the door`, kind: 'success' }))
        .catch((err: Error) => bus.emit('atlas:toast', { text: String(err?.message ?? err).replace(/^SenderError:\s*/, ''), kind: 'error' }));
    } else if (o.action === 'posts') bus.emit('atlas:open-town-life', { tab: 'posts' });
  }

  /** `__venue()` — what the lever, the registry and the counter are right now (probes read this). */
  private state(): Record<string, unknown> {
    const c = venueCounter();
    const f = this.lastFacts;
    const round = (a: WorldAnchor): Record<string, unknown> => ({ id: a.id, x: +a.x.toFixed(2), y: +a.y.toFixed(2), z: +a.z.toFixed(2), lx: +a.lx.toFixed(2), lz: +a.lz.toFixed(2), source: a.source });
    const me = { x: atlas.localX, z: atlas.localZ };
    const here = venueBindings(this.seed).filter((b) => Math.hypot(b.x - me.x, b.z - me.z) < 400).map((b) => ({
      site: b.site, kind: b.kind, key: b.key, x: b.x, z: b.z, yaw: b.yaw, w: b.w, d: b.d, title: venueTitle(b), cls: b.cls, slot: [b.slotX, b.slotZ], street: [b.streetX, b.streetZ],
      dist: +Math.hypot(b.x - me.x, b.z - me.z).toFixed(1), inside: insideVenue(b, me.x, me.z),
    }));
    return {
      enabled: venuesEnabled(), seed: this.seed, bindings: venueBindings(this.seed).length, near: here,
      applied: [...this.applied].map(([id, a]) => ({ id, kind: a.binding.kind, name: a.ent.name, orig: a.orig })),
      inside: c ? { id: c.id, kind: c.binding.kind, source: c.anchors.source, dStaff: Number.isFinite(c.dStaff) ? +c.dStaff.toFixed(2) : null, dCustomer: Number.isFinite(c.dCustomer) ? +c.dCustomer.toFixed(2) : null, anywhere: c.anywhere, staff: c.anchors.staff.map(round), customer: c.anchors.customer.map(round), queue: c.anchors.queue.map(round), spots: [...c.spots].map(([id, s]) => ({ id, role: s.role, x: +s.x.toFixed(2), z: +s.z.toFixed(2), lying: s.lying })) } : null,
      offer: this.offer, owns: this.offer ? interactFocus().owns(this.offer.id) : false,
      // who is shown where (the life layer draws them): versona id → role + the anchor's point
      spots: c ? [...c.spots].map(([id, q]) => ({ id, role: q.role, x: +q.x.toFixed(2), y: +q.y.toFixed(2), z: +q.z.toFixed(2), yaw: +q.yaw.toFixed(3) })) : [],
      facts: c && f ? { mine: f.mine, holdOther: f.holdOther, holder: f.holder, staff: f.staff, staffId: f.staffId === null ? null : f.staffId.toString(), waiting: f.waiting ? { need: f.waiting.need, arrived: f.waiting.arrived, versonaId: f.waiting.versonaId.toString() } : null } : null,
    };
  }
}
